import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, TrendingDown, Receipt, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/** Money summary for one project — shown only to roles that may see the cash position. */
export default function ProjectFinanceCard({ projectId }) {
  const { user } = useAuth();
  const [pos, setPos] = useState(null);
  const allowed = can(user, "cash_position.view");
  useEffect(() => {
    if (!allowed || !projectId) return;
    axios.get(`${API}/ledger/cash-position`, { params: { project_id: projectId } })
      .then(r => setPos(r.data.projects?.[0] || null)).catch(() => setPos(null));
  }, [projectId, allowed]);
  if (!allowed || !pos) return null;
  const net = pos.net_position || 0;
  const items = [
    { label: "Received", value: pos.received, sub: `${fmt(pos.billed)} billed · ${pos.receivable > 0 ? fmt(pos.receivable) + " due" : "settled"}`, icon: TrendingUp, cls: "text-emerald-700" },
    { label: "Paid to vendors", value: pos.vendor_paid, sub: `${fmt(pos.po_committed)} on POs · ${pos.vendor_outstanding > 0 ? fmt(pos.vendor_outstanding) + " unpaid" : "all paid"}`, icon: TrendingDown, cls: "text-red-700" },
    { label: "Expenses", value: pos.expenses, sub: `${pos.counts?.expenses || 0} entr${pos.counts?.expenses === 1 ? "y" : "ies"}`, icon: Receipt, cls: "text-amber-700" },
    { label: net >= 0 ? "Client money in hand" : "Funded by you", value: Math.abs(net), sub: `projected margin ${fmt(pos.projected_margin)}${pos.projected_margin_pct != null ? ` (${pos.projected_margin_pct}%)` : ""}`, icon: net >= 0 ? Wallet : AlertTriangle, cls: net >= 0 ? "text-emerald-700" : "text-red-700" },
  ];
  return (
    <div className="bg-white border border-[#E2E8F0] rounded p-5 mb-6" data-testid="project-finance-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-sm text-[#0A1128]">Money</h3>
        <Link to="/app/payments?tab=cash" className="text-[11px] text-[#2F7D52] hover:underline">Open payments →</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(it => (
          <div key={it.label} className="rounded border border-[#F1F5F9] bg-[#F8FAFC] p-3">
            <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${it.cls}`}><it.icon size={12} />{it.label}</div>
            <div className={`font-heading font-black text-lg ${it.cls}`}>{fmt(it.value)}</div>
            <div className="text-[10px] text-[#8AA496]">{it.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
